import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../Layout/DashboardLayout";
import DashboardTopbar from "../Layout/DashboardTopbar";
import useTokenBalance from "../hooks/useTokenBalance";
import { useEngineConfigContext } from "../Context/EngineConfigContext";
import { useAuthContext } from "../Context/AuthContext";
import EngineConfigCard from "./EngineConfig/EngineConfigCard";
import EngineConfigFilters from "./EngineConfig/EngineConfigFilters";
import EngineConfigAddCard from "./EngineConfig/EngineConfigAddCard";
import EngineConfigCreateModal from "./EngineConfig/EngineConfigCreateModal";
import { VERSION_OPTIONS } from "./EngineConfig/EngineConfigForm";
import {
  getPersistedEngineConfigUiState,
  saveEngineConfigPersistence
} from "../utils/engineConfigPersistence";

const persistedUi = getPersistedEngineConfigUiState();

const EngineConfigList = ({ initialCreateOpen = false }) => {
  const { list, setCurrentConfig, filters, setFilters, clearFilters, destroy, clearForm, formValues } =
    useEngineConfigContext();
  const {
    fetchUserAttributes,
    user,
    userAttributes: { length: userAttrLength }
  } = useAuthContext();
  const { tokenBalance } = useTokenBalance();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");
  const [fetching, setFetching] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(
    initialCreateOpen || persistedUi.createModalOpen
  );

  useEffect(() => {
    if (initialCreateOpen) {
      setCurrentConfig(null);
      clearForm();
      setCreateModalOpen(true);
    }
  }, [initialCreateOpen]);

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    const execute = async () => {
      if (userAttrLength === 0) {
        try {
          setFetching(true);
          await fetchUserAttributes();
        } finally {
          if (!cancelled) {
            setFetching(false);
          }
        }
      }
    };
    execute();

    return () => {
      cancelled = true;
    };
  }, [user, userAttrLength, fetchUserAttributes]);

  useEffect(() => {
    saveEngineConfigPersistence({
      filters,
      createModalOpen,
      formValues: createModalOpen ? formValues : null
    });
  }, [filters, createModalOpen, formValues]);

  const open = ({ id }) => {
    navigate(`/engine-config/${id}/edit`, { replace: true });
  };

  const openCreateModal = () => {
    setCurrentConfig(null);
    clearForm();
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    clearForm();
    setCreateModalOpen(false);
    if (location.pathname.endsWith("/create")) {
      navigate("/engine-config", { replace: true });
    }
  };

  const handleUse = (config) => {
    setCurrentConfig(config);
    navigate("/api-generator");
  };

  const handleDelete = async (config) => {
    if (confirm(t("Are you sure? You want to delete this engine configuration!"))) {
      await destroy(config.id);
      await fetchUserAttributes();
    }
  };

  const engineOptions = useMemo(() => {
    return list
      .map(({ type }) => type)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((e) => [e, e]);
  }, [list]);

  const urlOptions = useMemo(() => {
    return list
      .map(({ url }) => url)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((e) => [e, e]);
  }, [list]);

  const databaseOptions = useMemo(() => {
    return list
      .map(({ database }) => database)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((e) => [e, e]);
  }, [list]);

  const updateFilterValue = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const filteredList = useMemo(() => {
    return list.filter(({ name, type, version, url, database }) => {
      return (
        new RegExp(filters.search, "gi").test(name) &&
        (!filters.type || type === filters.type) &&
        (!filters.version || version === filters.version) &&
        (!filters.url || url === filters.url) &&
        (!filters.database || database === filters.database)
      );
    });
  }, [list, filters]);

  return (
    <DashboardLayout>
      <div className="dashboard-page">
        <DashboardTopbar title={t("Engine Configuration")} tokenBalance={tokenBalance} />
        <div className="page-content">
          <div className="page-header page-header-row">
            <div>
              <h1>{t("Engine Config")}</h1>
              <p>{t("Manage your Odoo server connections")}</p>
            </div>
            <div className="page-header-actions">
              <button type="button" className="btn btn-ghost" onClick={clearFilters}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                <span>{t("Clear")}</span>
              </button>
              {list.length < 10 && (
                <button type="button" className="btn btn-primary" onClick={openCreateModal}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span>{t("New Connection")}</span>
                </button>
              )}
            </div>
          </div>

          <EngineConfigFilters
            engineOptions={engineOptions}
            versionOptions={Object.entries(VERSION_OPTIONS)}
            urlOptions={urlOptions}
            databaseOptions={databaseOptions}
            filters={filters}
            onFilterChange={updateFilterValue}
          />

          {fetching ? (
            <div className="dashboard-loading">{t("Fetching...")}</div>
          ) : (
            <>
              <div className="grid-auto">
                {filteredList.map((engineConfig) => (
                  <EngineConfigCard
                    key={engineConfig.id}
                    config={engineConfig}
                    onEdit={open}
                    onDelete={handleDelete}
                    onUse={handleUse}
                  />
                ))}
                {list.length < 10 && <EngineConfigAddCard onClick={openCreateModal} />}
              </div>

              {!fetching && list.length === 0 && (
                <div className="dashboard-empty">
                  {t("There is no configuration here...")}
                </div>
              )}
              {!fetching && list.length > 0 && filteredList.length === 0 && (
                <div className="dashboard-empty">
                  {t("The engine configuration you're looking for couldn't be found!")}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EngineConfigCreateModal open={createModalOpen} onClose={closeCreateModal} />
    </DashboardLayout>
  );
};

EngineConfigList.propTypes = {
  initialCreateOpen: PropTypes.bool
};

EngineConfigList.defaultProps = {
  initialCreateOpen: false
};

export default EngineConfigList;
